import { ApiErrors, QueryResult } from '@betterment/js-runtime';
import { SambaAutoLink } from '@betterment/shared-components';
import { Box, Flex, Text } from '@betterment/style-closet-react';
import { take as _take, times as _times } from 'es-toolkit/compat';

import { useAccount } from './AccountProvider';
import CheckingCard from './CheckingCard';
import TransactionLoader from './TransactionLoader';
import TransactionsTable from './TransactionsTable';

import ErrorFallback from '../../Shared/components/ErrorFallback';
import { urlBuilder } from '../../Shared/urlBuilder';
import { DetailedTransaction } from '../data';

export interface Props {
  errors: ApiErrors | null;
  loadData: QueryResult<DetailedTransaction[]>['refetch'];
  isLoading: boolean;
  transactions: DetailedTransaction[] | null;
}

export const TRANSACTION_SUMMARY_LIMIT = 6;

const SummaryTransactions: React.FC<React.PropsWithChildren<Props>> = ({
  errors,
  loadData,
  isLoading,
  transactions,
}) => {
  const shouldShowViewAllLink = transactions && transactions.length > TRANSACTION_SUMMARY_LIMIT;
  const limitedTransactions = _take(transactions, TRANSACTION_SUMMARY_LIMIT);
  const shouldShowNoTransactions = !isLoading && transactions && transactions.length === 0;
  const { account } = useAccount();

  return (
    <CheckingCard mb={[6, 8]}>
      <Box bg="bmtWhite">
        <Flex
          pb="6"
          pt="8"
          borderBottom="1px solid"
          borderColor="bmtGrey20"
          justifyContent="space-between"
          alignItems="center"
        >
          <Text as="h2" fontSize="3" fontWeight="medium" m="0">
            Recent transactions
          </Text>
          {shouldShowViewAllLink && (
            <SambaAutoLink
              to={urlBuilder.getAppCheckingAccountTransactions.absolute({ accountId: account.id })}
              title="View All Transactions"
            >
              View all
            </SambaAutoLink>
          )}
        </Flex>
        {!isLoading && <TransactionsTable transactions={limitedTransactions} />}
      </Box>

      {errors && <ErrorFallback message="Unable to load transactions." handleReload={loadData} />}

      {shouldShowNoTransactions && (
        <Box bg="bmtWhite" px={[6, null, 8]} py="135px">
          <Text as="p" fontWeight="bold" textAlign="center" mb="3">
            You have no transactions
          </Text>
          <Text as="p" fontSize="1" textAlign="center" color="bmtGrey50">
            Fund your account to start spending.
          </Text>
        </Box>
      )}

      {isLoading && (
        <Box bg="bmtWhite">
          {_times(TRANSACTION_SUMMARY_LIMIT, value => (
            <TransactionLoader key={value} />
          ))}
        </Box>
      )}
    </CheckingCard>
  );
};

export default SummaryTransactions;
